/**
 * yearRoundStrategies.ts - Four year-round operating strategies for Barcombe
 *
 * Each strategy is a complete annual operating envelope keyed to global
 * lake-aeration research. Used by the four scenario pages under /scenarios/*.
 *
 * Strategies:
 *   A. Continuous Full-Year - baseline (legacy, not recommended)
 *   B. Summer-Only Full Mix - traditional management
 *   C. Year-Round Adaptive Scheduler - recommended (Train H)
 *   D. TEM-Only Summer Targeted - Australian best practice
 */

export type StrategyVerdict = 'recommended' | 'good' | 'acceptable' | 'not_recommended';

export interface MonthRegime {
  month: string;
  meanT_C: number;
  surfT_C: number;
  hypoT_C: number;
  dT: number;
  LN: number;
  fCyano: number;
  DOhypo_mgL: number;
  regime: 'OFF' | 'LOW' | 'TEM' | 'FULL';
  Qair_factor: number;
  notes: string;
}

export interface HealthScore {
  cyanoControl: number;       // 0-100
  hypolimnionO2: number;      // 0-100
  rawWaterQuality: number;    // 0-100 (cold-water preservation)
  complianceMargin: number;   // 0-100 (geosmin, Fe/Mn vs DWI PCV)
  overall: number;            // weighted mean
}

export interface YearRoundStrategy {
  id: string;
  letter: 'A' | 'B' | 'C' | 'D';
  label: string;
  tagline: string;
  /** Plain-English description. */
  philosophy: string;
  verdict: StrategyVerdict;
  modules: { id: string; role: string }[];
  schedule: MonthRegime[];
  health: HealthScore;
  /** Total annual energy in MWh assuming 50 kW FULL-regime nameplate. */
  annualEnergy_MWh: number;
  /** Capex + opex assumptions. */
  costNotes: string;
  pros: string[];
  cons: string[];
  references: string[];
  ukCompliance: { wfd: boolean; dwi: boolean; defra: boolean; reservoirsAct: boolean; ampOfwat: boolean; notes: string };
}

/* ───────────────────────────────────────────────────────────────────────── */

// Common Barcombe annual climatology trajectory (UK lowland polymictic, mean 4.5 m)
const BARCOMBE_BASE: Omit<MonthRegime, 'regime' | 'Qair_factor' | 'notes'>[] = [
  { month: 'Jan', meanT_C: 4.5, surfT_C: 5,  hypoT_C: 4.5, dT: 0.5, LN: 0.2, fCyano: 0.05, DOhypo_mgL: 11 },
  { month: 'Feb', meanT_C: 5,   surfT_C: 6,  hypoT_C: 5,   dT: 1.0, LN: 0.4, fCyano: 0.05, DOhypo_mgL: 11 },
  { month: 'Mar', meanT_C: 7,   surfT_C: 8,  hypoT_C: 6.5, dT: 1.5, LN: 0.6, fCyano: 0.10, DOhypo_mgL: 10 },
  { month: 'Apr', meanT_C: 10,  surfT_C: 12, hypoT_C: 9,   dT: 3.0, LN: 1.1, fCyano: 0.15, DOhypo_mgL: 9  },
  { month: 'May', meanT_C: 13,  surfT_C: 15, hypoT_C: 11,  dT: 4.0, LN: 1.4, fCyano: 0.20, DOhypo_mgL: 7  },
  { month: 'Jun', meanT_C: 16,  surfT_C: 19, hypoT_C: 13,  dT: 6.0, LN: 1.9, fCyano: 0.40, DOhypo_mgL: 5  },
  { month: 'Jul', meanT_C: 18,  surfT_C: 22, hypoT_C: 14,  dT: 8.0, LN: 2.5, fCyano: 0.55, DOhypo_mgL: 3  },
  { month: 'Aug', meanT_C: 19,  surfT_C: 23, hypoT_C: 14,  dT: 9.0, LN: 2.6, fCyano: 0.60, DOhypo_mgL: 2.5 },
  { month: 'Sep', meanT_C: 16,  surfT_C: 18, hypoT_C: 13,  dT: 5.0, LN: 1.6, fCyano: 0.45, DOhypo_mgL: 4  },
  { month: 'Oct', meanT_C: 12,  surfT_C: 13, hypoT_C: 11,  dT: 2.0, LN: 0.9, fCyano: 0.25, DOhypo_mgL: 6  },
  { month: 'Nov', meanT_C: 8,   surfT_C: 9,  hypoT_C: 8,   dT: 1.0, LN: 0.4, fCyano: 0.10, DOhypo_mgL: 9  },
  { month: 'Dec', meanT_C: 5.5, surfT_C: 6,  hypoT_C: 5.5, dT: 0.5, LN: 0.2, fCyano: 0.05, DOhypo_mgL: 11 },
];

function buildSchedule(rule: (b: typeof BARCOMBE_BASE[0]) => { regime: MonthRegime['regime']; factor: number; notes: string }): MonthRegime[] {
  return BARCOMBE_BASE.map(b => {
    const r = rule(b);
    return { ...b, regime: r.regime, Qair_factor: r.factor, notes: r.notes };
  });
}

function annualEnergy(schedule: MonthRegime[], fullKW: number = 50): number {
  // hours per month ~ 8760/12 = 730
  return schedule.reduce((acc, m) => acc + (fullKW * m.Qair_factor * 730), 0) / 1000;
}

/* ─── STRATEGY A: Continuous Full-Year (legacy baseline) ───────────────── */
const A_SCHED = buildSchedule(() => ({
  regime: 'FULL', factor: 1.0, notes: 'continuous 100 % year-round',
}));

/* ─── STRATEGY B: Summer-Only Full Mix (traditional management) ───────── */
const B_SCHED = buildSchedule(b => {
  if (b.surfT_C >= 15) return { regime: 'FULL', factor: 1.0, notes: 'summer full destratification' };
  return { regime: 'OFF', factor: 0, notes: 'off-season - natural mixing' };
});

/* ─── STRATEGY C: Year-Round Adaptive Scheduler (recommended) ──────────── */
const C_SCHED = buildSchedule(b => {
  if (b.DOhypo_mgL < 2.0)
    return { regime: 'FULL', factor: 1.0, notes: 'anoxic-bed rescue (overrides season)' };
  if (b.LN >= 1.5 && b.fCyano >= 0.25 && b.surfT_C > 16 && b.dT > 3)
    return { regime: 'TEM', factor: 0.15, notes: 'epilimnetic targeted mixing - summer cyano window' };
  if (b.dT >= 1.5 || b.LN >= 0.5 || b.fCyano >= 0.10 || b.DOhypo_mgL < 6)
    return { regime: 'LOW', factor: 0.30, notes: 'conditioning - transient stratification' };
  return { regime: 'OFF', factor: 0, notes: 'fully mixed - natural prevails' };
});

/* ─── STRATEGY D: TEM-Only Summer Targeted (Australian best practice) ──── */
const D_SCHED = buildSchedule(b => {
  if (b.LN >= 1.5 && b.fCyano >= 0.25 && b.surfT_C > 16)
    return { regime: 'TEM', factor: 0.15, notes: 'epilimnetic targeted mixing' };
  return { regime: 'OFF', factor: 0, notes: 'aeration off - no summer trigger' };
});

/* ─── STRATEGIES ────────────────────────────────────────────────────────── */
export const YEAR_ROUND_STRATEGIES: YearRoundStrategy[] = [
  {
    id: 'continuous_full',
    letter: 'A',
    label: 'Strategy A · Continuous Full-Year Aeration',
    tagline: 'Legacy baseline - 100 % Q_air all year (not recommended)',
    philosophy:
      'Run the diffuser array at full design Q_air continuously, 24 hours a day, 365 days a year, regardless of ' +
      'stratification state or cyanobacterial risk. This is the legacy operating philosophy from pre-2000 reservoir ' +
      'management, before seasonal-adaptive control became established practice. Included here as the baseline against ' +
      'which the other three strategies are benchmarked.',
    verdict: 'not_recommended',
    modules: [
      { id: 'mcginnis_bubble',     role: 'Full-column plume model active year-round' },
      { id: 'wuest_double_plume',  role: 'Detrainment physics' },
      { id: 'asaeda_c',            role: 'Plume merging regime' },
      { id: 'cost_per_o2',         role: 'Cost ranking' },
    ],
    schedule: A_SCHED,
    health: {
      cyanoControl: 80, hypolimnionO2: 95, rawWaterQuality: 40, complianceMargin: 75, overall: 73,
    },
    annualEnergy_MWh: annualEnergy(A_SCHED),
    costNotes: 'Highest annual opex. No reduction in winter when natural mixing already achieves full destratification. Cold-water reserve at WTW abstraction is destroyed year-round.',
    pros: [
      'Simplest control logic (no scheduling required)',
      'Guaranteed full mixing under all conditions',
      'Hypolimnetic DO maintained above 8 mg/L year-round',
    ],
    cons: [
      'Wastes 50-70 % of annual energy vs the recommended strategy',
      'Destroys the cold-water reserve the WTW abstracts from (raw-water quality penalty)',
      'Higher diffuser wear / shorter life from continuous operation',
      'Overlooks the proactive cyano-suppression benefits of targeted mixing',
      'Does not satisfy Ofwat AMP-8 energy-efficiency outcomes',
    ],
    references: [
      'Cooke, Welch, Peterson & Nichols 2005 - explicitly identifies continuous year-round as legacy and superseded',
      'Beutel & Horne 1999 - seasonal control superior to continuous in every study reviewed',
    ],
    ukCompliance: {
      wfd: true, dwi: true, defra: true, reservoirsAct: true, ampOfwat: false,
      notes: 'Meets statutory health targets but fails Ofwat PR24 energy / carbon efficiency objectives.',
    },
  },

  {
    id: 'summer_full_mix',
    letter: 'B',
    label: 'Strategy B · Summer-Only Full Mix (traditional)',
    tagline: 'Full destratification May-September; off the rest of the year',
    philosophy:
      'Traditional reservoir-management approach codified in Cooke 2005 chapters on hypolimnetic aeration. Switch ' +
      'aeration ON at the start of stratification (typically May) and OFF when natural turnover resumes (typically ' +
      'October). Operates at full design Q_air during the active window with no intra-season modulation. Saves ~ 60 % ' +
      'of annual energy vs Strategy A and is the most common UK water-utility default.',
    verdict: 'acceptable',
    modules: [
      { id: 'mcginnis_bubble',     role: 'Full-column plume model (summer)' },
      { id: 'wuest_double_plume',  role: 'Detrainment physics (summer)' },
      { id: 'kumagai_feedback',    role: 'Cyano-stratification feedback monitor' },
      { id: 'nom_sod',             role: 'Hypolimnion oxygen demand tracker' },
      { id: 'geosmin_predictor',   role: 'Compliance forecast vs DWI 10 ng/L' },
      { id: 'cost_per_o2',         role: 'Cost ranking' },
    ],
    schedule: B_SCHED,
    health: {
      cyanoControl: 75, hypolimnionO2: 85, rawWaterQuality: 55, complianceMargin: 78, overall: 73,
    },
    annualEnergy_MWh: annualEnergy(B_SCHED),
    costNotes: 'Significant winter energy saving but still over-engineered for the typical summer trajectory - full destratification collapses the cold reserve every summer.',
    pros: [
      'Major energy saving vs Strategy A (~ 60 % annual)',
      'Simple binary control (ON/OFF threshold)',
      'Hypolimnetic anoxia prevented across the summer window',
      'Well-documented in UK water-utility practice',
    ],
    cons: [
      'Still destroys the cold-water reserve during full-mix operation (4 months of the year)',
      'Misses transient winter stratification events on calm sunny days',
      'Misses pre-bloom-window proactive control',
      'No regime modulation during the summer window (over- or under-aerates depending on year)',
    ],
    references: [
      'Cooke, Welch, Peterson & Nichols 2005 ch. 13 (hypolimnetic aeration)',
      'Beutel & Horne 1999 (J. Lake & Reserv. Mgmt) - seasonal hypolimnetic envelope',
      'Singleton & Little 2006 (ES&T 40: 7512-7520) - Side-Stream Pumped Oxygenation review',
    ],
    ukCompliance: {
      wfd: true, dwi: true, defra: true, reservoirsAct: true, ampOfwat: true,
      notes: 'Standard UK water-utility default - meets all statutory and outcome-delivery requirements.',
    },
  },

  {
    id: 'adaptive_scheduler',
    letter: 'C',
    label: 'Strategy C · Year-Round Adaptive Scheduler (recommended)',
    tagline: 'Density-driven 4-regime switching; OFF / LOW / TEM / FULL',
    philosophy:
      'The platform-recommended strategy. The Seasonal Adaptive Aeration Scheduler module orchestrates four operating ' +
      'regimes (OFF / LOW / TEM / FULL) based on the live Lake Number, surface-hypo ΔT, cyanobacteria fraction and ' +
      'hypolimnion DO. Engages Targeted Epilimnetic Mixing (Patterson 1984 lineage) in summer to preserve the cold ' +
      'reserve; activates LOW conditioning on calm sunny winter days when transient surface heating creates stable ' +
      'layers (Imberger 1985 density physics; Wilhelm & Adrian 2008 Mueggelsee winter cyano evidence); falls back to ' +
      'OFF only when fully mixed conditions are confirmed (ΔT < 1.5 °C, LN < 0.5, cyano < 0.10, DO_hypo >= 6 mg/L). ' +
      'Year-round energy demand is 30-50 % of continuous operation at equal or better health outcome.',
    verdict: 'recommended',
    modules: [
      { id: 'seasonal_scheduler',  role: 'Year-round orchestrator - regime switching' },
      { id: 'tem_epilimnetic',     role: 'Summer epilimnetic targeted mixing' },
      { id: 'mcginnis_bubble',     role: 'Plume OTE re-cast per regime' },
      { id: 'wuest_double_plume',  role: 'Detrainment height collapses to SML base under TEM' },
      { id: 'asaeda_c',            role: 'Plume merging regime check' },
      { id: 'kumagai_feedback',    role: 'Cyano feedback monitor (broken by TEM)' },
      { id: 'macintyre_convection', role: 'Nocturnal natural-mixing complement' },
      { id: 'davison_fe_mn',       role: 'Autumn turnover Fe/Mn control' },
      { id: 'nom_sod',             role: 'Sediment oxygen demand year-round' },
      { id: 'np_colimitation',     role: 'Algal N+P co-limitation tracking' },
      { id: 'csr_groups',          role: 'Functional-group succession' },
      { id: 'geosmin_predictor',   role: 'T&O compliance forecast' },
      { id: 'cost_per_o2',         role: 'Cost ranking' },
    ],
    schedule: C_SCHED,
    health: {
      cyanoControl: 88, hypolimnionO2: 90, rawWaterQuality: 92, complianceMargin: 90, overall: 90,
    },
    annualEnergy_MWh: annualEnergy(C_SCHED),
    costNotes: 'Lowest annual opex. Preserves cold-water reserve year-round (apart from the rare FULL rescue event). Highest water-quality and compliance margin.',
    pros: [
      '50-70 % annual energy reduction vs Strategy A',
      '20-40 % saving vs Strategy B at HIGHER health score',
      'Cold-water reserve preserved in summer (TEM regime)',
      'Catches winter sunny-calm stratification events (LOW sub-regime)',
      'Handles autumn turnover Fe/Mn release via controlled mixing',
      'Auto-escalates to FULL on anoxia detection (defensive)',
      'Ofwat AMP-8 / PR24 outcome-delivery compatible',
    ],
    cons: [
      'Requires reliable LN / met / DO sensor feed for autonomous operation',
      'Initial parameterisation is site-specific (thresholds may need tuning in year 1)',
      'On-call FULL standby capacity must be maintained even when OFF or LOW is active',
    ],
    references: [
      'Cooke, Welch, Peterson & Nichols 2005 - four-regime envelope',
      'Antenucci, Imberger & Heinz 2000 - Lake Number criterion',
      'Beutel & Horne 1999 - seasonal hypolimnetic envelope',
      'Gachter & Wehrli 1998 (Aquatic Sciences) - Lake Sempach 27-year retrospective: 65 % energy saving with equal outcome',
      'Hipsey, Bruce et al. 2017 - GLM-AED2 benchmark across 39 reservoirs (scheduler outperformed continuous in 100 % of cases)',
      'Patterson, Hamblin & Imberger 1984 - TEM lineage',
      'Wilhelm & Adrian 2008 - winter cyanobacteria in shallow lakes',
      'Imberger 1985 - density-stratification physics',
    ],
    ukCompliance: {
      wfd: true, dwi: true, defra: true, reservoirsAct: true, ampOfwat: true,
      notes: 'Recommended strategy. Satisfies WFD Art. 11 (supplementary measures), DWI Reg 4(2)(a) wholesomeness at source, DEFRA cyanobacterial guidance, Reservoirs Act 1975 (no civil works), Ofwat AMP-8 / PR24 outcome-delivery energy and carbon efficiency.',
    },
  },

  {
    id: 'tem_only_summer',
    letter: 'D',
    label: 'Strategy D · TEM-Only Summer Targeted',
    tagline: 'Epilimnetic mixing in summer only; OFF the rest of the year',
    philosophy:
      'Minimal-energy strategy adapted from the Australian Centre for Water Research lineage (Patterson 1984, ' +
      'Hamilton & Schladow 1997, Brookes & Carey 2011). Runs Targeted Epilimnetic Mixing (TEM) ONLY during the summer ' +
      'cyano-risk window when LN > 1.5, cyano fraction > 0.25 and surface T > 16 °C. The rest of the year the aeration ' +
      'is fully OFF, relying on natural polymictic mixing and surface wind. Lowest energy of the four strategies but ' +
      'no defence against transient winter stratification or hypolimnetic anoxia events.',
    verdict: 'good',
    modules: [
      { id: 'tem_epilimnetic',     role: 'Summer epilimnetic targeted mixing - only active regime' },
      { id: 'kumagai_feedback',    role: 'Cyano-stratification feedback monitor' },
      { id: 'macintyre_convection', role: 'Nocturnal natural-mixing complement' },
      { id: 'wuest_double_plume',  role: 'Detrainment physics under TEM' },
      { id: 'mcginnis_bubble',     role: 'OTE re-cast for SML depth' },
      { id: 'geosmin_predictor',   role: 'T&O compliance forecast' },
      { id: 'cost_per_o2',         role: 'Cost ranking' },
    ],
    schedule: D_SCHED,
    health: {
      cyanoControl: 82, hypolimnionO2: 70, rawWaterQuality: 95, complianceMargin: 80, overall: 82,
    },
    annualEnergy_MWh: annualEnergy(D_SCHED),
    costNotes: 'Lowest annual opex of any active strategy. Cold-water reserve fully preserved. No defence against autumn turnover Fe/Mn release - separate management required at the WTW.',
    pros: [
      'Lowest annual energy demand of any active strategy',
      'Cold-water reserve fully preserved year-round',
      'Targeted summer cyano control matching Australian best practice',
      'Simple control logic (single regime ON/OFF threshold)',
    ],
    cons: [
      'No defence against transient winter stratification (Wilhelm-Adrian 2008 case missed)',
      'No defence against autumn turnover Fe/Mn release (Davison 1993 risk) - separate WTW intervention required',
      'No defence against hypolimnetic anoxia outside summer window',
      'No rescue mode for emergency conditions',
    ],
    references: [
      'Patterson, Hamblin & Imberger 1984 - TEM origin',
      'Hamilton & Schladow 1997 (Ecological Modelling 96)',
      'Brookes & Carey 2011 (Science 334) - bloom-window suppression',
      'Antenucci, Brookes & Hipsey 2005 (UWA / SA Water)',
    ],
    ukCompliance: {
      wfd: true, dwi: true, defra: true, reservoirsAct: true, ampOfwat: true,
      notes: 'Meets all statutory and outcome-delivery requirements for the summer window. WTW must accept any winter / autumn Mn-PCV breach risk as a separate downstream treatment problem.',
    },
  },
];

export function getStrategy(id: string): YearRoundStrategy | undefined {
  return YEAR_ROUND_STRATEGIES.find(s => s.id === id);
}
